/** @format */

import React, { useEffect, useRef, useState, useCallback } from 'react'
import styled from 'styled-components'
import { Controlled as CodeMirror } from 'react-codemirror2'
import CodeMirrorLib from 'codemirror'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/python/python'
// 导入自动完成功能
import 'codemirror/addon/hint/show-hint'
import 'codemirror/addon/hint/show-hint.css'
import {
  Button,
  Card,
  Copy,
  Drawer,
  Message,
  Radio,
  Tip,
  Form,
  FormItem,
  Input
} from '@king-design/react'
import JsonEditor, { isValidJsonObject } from 'src/components/JsonCodemirror'
import { getScript, saveScript } from 'src/api/ApiEventOpsScript'
import { testScript } from 'src/api/ApiEventOpsFunction'
import { useAsync, useAsyncFn } from 'react-use'
import { TriggerTypeEnum } from '../../container'
import { isEmpty } from 'lodash'
import { ProcessRecordNodeInputProps } from 'src/pages/EventOps/Scene/container'
import { request } from 'src/utils'

// 脚本
export interface ScriptItemProp {
  ScriptID: number
  Content: string
  Outputs: string[]
}
interface TriggerCodeMirrorProps {
  className?: string
  scriptID: number
  triggerType?: keyof typeof TriggerTypeEnum // 触发器类型
  inputs?: ProcessRecordNodeInputProps[]
  onChange?: (scriptID: number, content: string, lang: 'python3') => void
  readOnly?: boolean
}
const initTestResult = {
  err_buffer: '',
  out_buffer: ''
}

const TriggerCodeMirror: React.FC<TriggerCodeMirrorProps> = ({
  className,
  scriptID,
  triggerType,
  onChange,
  readOnly,
  inputs
}) => {
  const scriptIDRef = useRef<number>()
  const editorRef = useRef<CodeMirrorLib.Editor | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [content, setContent] = useState('')
  // 临时存储内容
  const [temContent, setTemContent] = useState<string>('')
  // 触发器-测试
  const [testVisible, setTestVisible] = useState(false)
  const [testContent, setTestContent] = useState('')
  const [testResult, setTestResult] = useState(initTestResult)
  // 场景-数据处理-测试
  const formRef = useRef(null)
  const [secneValues, setSecneValues] = useState<{ [key: string]: string }>({})

  // 公共函数list
  const { value: funcHits } = useAsync(async () => {
    const result = await request('/oos/?Action=ListScript&Version=2025-01-01', {
      data: {
        Type: 'func',
        Limit: 9999,
        Offset: 0
      },
      method: 'POST'
    })
    return result?.data?.Data?.Info || []
  })

  // 创建基于 testHits 的自定义提示函数
  const createCustomHint = useCallback(() => {
    return (cm: CodeMirrorLib.Editor) => {
      const cursor = cm.getCursor()
      const token = cm.getTokenAt(cursor)
      const start = token.start
      const end = cursor.ch
      const line = cm.getLine(cursor.line)
      const currentWord = line.slice(start, end)

      // 过滤出匹配当前语言的函数
      let filteredHits = funcHits.filter(hit => hit.lang === 'python3')

      // 如果没有匹配当前语言的数据，则显示所有语言的提示（用于 shell 语言等）
      if (filteredHits.length === 0) {
        filteredHits = funcHits
      }

      // 根据当前输入的单词匹配 sign 值（优先显示 startsWith 的结果）
      const wordLower = currentWord.toLowerCase()
      const matchedHits = filteredHits.filter(hit => {
        const signLower = hit.Sign?.toLowerCase()
        return signLower.startsWith(wordLower) || signLower.includes(wordLower)
      })

      // 排序：startsWith 的排在前面
      const sortedHits = matchedHits.sort((a, b) => {
        const aStartsWith = a.Sign?.toLowerCase().startsWith(wordLower)
        const bStartsWith = b.Sign?.toLowerCase().startsWith(wordLower)
        if (aStartsWith && !bStartsWith) return -1
        if (!aStartsWith && bStartsWith) return 1
        return a.Sign?.localeCompare(b.Sign)
      })

      const suggestions = sortedHits
        .map(hit => {
          // 如果有 remark，显示 sign + 空格 + remark，否则只显示 sign
          const displayText = hit.Remark ? `${hit.Sign || ''} ${hit.Remark || ''}` : hit.Sign || ''

          // 自定义插入到编辑器中的内容：函数名 + 参数模板
          const params = hit.Extra?.Params || []
          let insertText = hit.Sign
          // 如果有参数，插入函数调用模板
          if (params.length > 0) {
            const paramPlaceholders = params.map(p => {
              // 如果有默认值，使用默认值，否则使用参数名
              if (p.default !== undefined && p.default !== null && p.default !== '-') {
                return p.default
              }
              return p.Key
            })
            insertText = `${hit.Sign}(${paramPlaceholders.join(',')})`
          }

          return {
            text: insertText, // 插入到编辑器中的内容
            displayText: displayText, // 提示框中显示的内容
            className: 'cm-function',
            render: (element: HTMLElement, self: any, data: any) => {
              // 自定义渲染，支持显示描述
              const signSpan = document.createElement('span')
              signSpan.className = 'hint-sign'
              signSpan.textContent = hit.Sign

              if (hit.Remark) {
                const remarkSpan = document.createElement('span')
                remarkSpan.className = 'hint-remark'
                remarkSpan.textContent = hit.Remark
                element.appendChild(signSpan)
                element.appendChild(remarkSpan)
              } else {
                element.appendChild(signSpan)
              }
            }
          }
        })
        .slice(0, 50) // 限制最多显示50个结果

      // 如果没有匹配项，返回 null 不显示提示
      if (suggestions.length === 0) {
        return null
      }

      return {
        list: suggestions,
        from: CodeMirrorLib.Pos(cursor.line, start),
        to: CodeMirrorLib.Pos(cursor.line, end)
      }
    }
  }, [funcHits])

  // 事件聚合类型
  useEffect(() => {
    if (testVisible) {
      if (triggerType === 'monitor_event_agg') {
        const formatted = JSON.stringify(
          {
            key: '请在此处输入告警聚合key',
            items: [{}, {}]
          },
          null,
          2
        )
        setTestContent(formatted)
        setSecneValues(undefined)
      } else {
        setTestContent('')
        if (inputs?.length) {
          const initValues = {}
          inputs.forEach(item => {
            if (item) {
              initValues[item.Key] = item.Type === 'fixed' ? item.Value : ''
            }
          })
          setSecneValues(initValues)
        } else {
          setSecneValues({})
        }
      }
    }
  }, [testVisible, triggerType, inputs])

  // 初始化获取脚本
  const { value: scriptResult } = useAsync(async () => {
    if (scriptID && scriptID !== scriptIDRef.current) {
      const result = await getScript(scriptID)
      scriptIDRef.current = result.ID
      return result
    } else {
      return {
        Lang: 'python3',
        Content: ''
      }
    }
  }, [scriptID])

  useEffect(() => {
    setTemContent(scriptResult?.Content || '')
    setContent(scriptResult?.Content || '')
  }, [scriptResult])

  const [, fetchSaveScript] = useAsyncFn(
    async (c = content, id = scriptIDRef.current) => {
      if (!readOnly) {
        if (c) {
          const result = await saveScript({
            Type: 'main',
            ID: id,
            Content: c,
            Lang: 'python3'
          })
          if (result) {
            onChange?.(result.ID, c, 'python3')
          }
        }
      }
    },
    [scriptID, content, scriptResult]
  )

  // 测试
  const fetchTestScript = async () => {
    const { isValid, error, parsed } = isValidJsonObject(testContent)
    if (triggerType === 'monitor_event_agg' && !Array.isArray(parsed?.items)) {
      Message.error('items必须为数组')
      return
    }
    if (isValid) {
      const result = await testScript({
        TryParse: isEmpty(secneValues),
        Lang: 'python3',
        Code: content,
        Inputs: triggerType ? parsed : { ...secneValues }
      })
      setTestResult({
        err_buffer: result?.ErrBuffer || '',
        out_buffer: result?.OutBuffer || ''
      })
    } else {
      Message.error(error || 'JSON 格式错误')
      setTestResult({
        err_buffer: '',
        out_buffer: ''
      })
    }
  }

  return funcHits?.length ? (
    <div className={className}>
      <Card
        type="border"
        slotHeader={
          <div className="trigger_code_mirror_header">
            <div className="radio_group">
              <Radio trueValue="python3" name="lange" disabled={readOnly} value={'python3'}>
                python3
              </Radio>
            </div>
            {!readOnly ? (
              <div className="ksc-space">
                <Copy text={temContent}>
                  <a>复制</a>
                </Copy>
                <a
                  onClick={() => {
                    setTestVisible(true)
                  }}
                >
                  测试
                </a>
              </div>
            ) : null}
          </div>
        }
      >
        <CodeMirror
          value={temContent}
          onBeforeChange={(_editor, _data, value) => {
            setTemContent(value)
          }}
          onBlur={editor => {
            const currentValue = editor.getValue()
            setContent(currentValue)
            fetchSaveScript(currentValue)
          }}
          options={{
            mode: 'python',
            theme: 'material',
            lineNumbers: true,
            lineWrapping: true,
            foldGutter: true,
            gutters: ['CodeMirror-linenumbers'],
            // 自动完成配置
            hintOptions: {
              hint: createCustomHint(),
              completeSingle: false, // 不自动完成单个匹配项
              alignWithWord: true // 与单词对齐
            },
            // 快捷键配置
            extraKeys: {
              'Shift-Tab': 'indentLess' // Shift+Tab 减少缩进
            }
          }}
          editorDidMount={editor => {
            editorRef.current = editor

            // 注入全局样式，确保提示框可见
            if (!document.getElementById('codemirror-hint-styles')) {
              const style = document.createElement('style')
              style.id = 'codemirror-hint-styles'
              style.textContent = `
                .CodeMirror-hints {
                  position: absolute !important;
                  z-index: 10000 !important;
                  overflow: hidden;
                  list-style: none;
                  margin: 0;
                  padding: 2px;
                  min-width: 250px;
                  max-width: 500px;
                  max-height: 20em;
                  overflow-y: auto;
                  background: #2b2b2b !important;
                  border: 1px solid #555 !important;
                  border-radius: 4px;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                  font-size: 14px;
                  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                }

                .CodeMirror-hint {
                  margin: 0;
                  border-radius: 2px;
                  white-space: pre;
                  color: #d4d4d4 !important;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                }

                .CodeMirror-hint-active {
                  background: #094771 !important;
                  color: #fff !important;
                }

                .CodeMirror-hint-active .hint-remark {
                  color: #ccc !important;
                }

                .hint-sign {
                  font-weight: 500;
                  flex: 0 0 auto;
                }

                .hint-remark {
                  color: #999;
                  margin-left: 8px;
                  flex: 1 1 auto;
                  text-align: right;
                }
              `
              document.head.appendChild(style)
            }

            // 监听输入事件，自动触发提示
            const handleInput = () => {
              // 清除之前的定时器
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
              }
              // 延迟触发提示，避免频繁触发
              timeoutRef.current = setTimeout(() => {
                const cursor = editor.getCursor()
                const token = editor.getTokenAt(cursor)
                const line = editor.getLine(cursor.line)
                const currentWord = line.slice(token.start, cursor.ch)

                // 如果当前输入的是字母、数字或下划线，且长度大于0，则显示提示
                if (/^[a-zA-Z0-9_]+$/.test(currentWord) && currentWord.length > 0) {
                  // 使用 showHint 方法显示提示
                  CodeMirrorLib.showHint(editor, createCustomHint(), {
                    completeSingle: false,
                    alignWithWord: true
                  })
                }
              }, 200) // 200ms 延迟，更快响应
            }

            // 监听多种事件以确保提示能正常触发
            editor.on('inputRead', handleInput)
            editor.on('change', handleInput)
            editor.on('cursorActivity', handleInput)
          }}
        />
      </Card>
      <Drawer
        value={testVisible}
        width={600}
        title="测试"
        slotFooter={
          <div>
            <Button
              onClick={() => {
                setTestContent('')
                setTestVisible(false)
                setTestResult(initTestResult)
              }}
            >
              关闭
            </Button>
            <Button
              type="primary"
              onClick={() => {
                fetchTestScript()
              }}
            >
              提交测试
            </Button>
          </div>
        }
        onChangeValue={show => {
          if (!show) {
            setTestContent('')
            setTestVisible(show)
            setTestResult(initTestResult)
          }
        }}
      >
        <div className={className}>
          <Tip type="warning" showIcon closable>
            提交测试后，系统将按照您设定的操作进行执行，请严格确认是否会对线上业务产生影响，谨慎调试。
          </Tip>
          <div className="test_content">
            <p>输入参数：</p>
            {triggerType ? (
              <>
                <p className="test_content_extra">
                  请输入原始告警JSON数据，可从[触发规则-&gt;查看历史事件]面板中复制告警数据。
                </p>
                <JsonEditor
                  value={testContent}
                  onChange={value => {
                    setTestContent(value)
                  }}
                />
              </>
            ) : (
              <Form ref={formRef} labelWidth={90}>
                {inputs
                  ?.filter(item => item)
                  .map((input, index) => {
                    return (
                      <FormItem key={`${input.Key}_${index}`} label={input.Key}>
                        <Input
                          disabled={input.Type === 'fixed'}
                          type={input.Secret ? 'password' : 'text'}
                          value={secneValues[input.Key]}
                          onChangeValue={value => {
                            setSecneValues({ ...secneValues, [input.Key]: value })
                          }}
                        />
                      </FormItem>
                    )
                  })}
              </Form>
            )}
            <div className="rest_result">
              <p style={{ margin: '16px 0 0 0' }}>测试结果：</p>
              <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                {testResult?.out_buffer || ''}
              </pre>
              {testResult?.err_buffer && (
                <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                  {testResult?.err_buffer || ''}
                </pre>
              )}
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  ) : null
}

export default styled(TriggerCodeMirror)`
  .k-warning {
    margin-bottom: 16px;
  }
  .k-card-header {
    height: 32px;
    padding: 0 12px;
    .trigger_code_mirror_header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      .k-radio {
        margin-right: 8px;
      }
      .ksc-space {
        display: flex;
        align-items: center;
        gap: 8px;
        .copy-btn {
          margin-right: 8px;
        }
      }
    }
  }
  .k-card-body {
    padding: 0 !important;
  }
  .test_content_extra {
    color: #999;
  }
  .CodeMirror-scroll {
    .CodeMirror-sizer {
      margin-left: 29px !important;
    }
  }
`
